import React from 'react';

import { EvidencePostureChip, formatEvidenceDateTime } from '../evidencePosture';
import {
  operationalEvidenceSummary,
  reliabilityVerdictChipClassName,
  validationCoverageSummary,
} from '../reliability';
import { buildRunTraceHref } from '../data';
import {
  EvidenceNeededGuidanceCard,
  buildWorkflowEvidenceGuidance,
} from '../evidenceNeededGuidance';
import { WorkflowImprovementLoopPanel } from './ImprovementLoopPanel';
import SuspiciousTracePackPanel from './SuspiciousTracePackPanel';
import { RuntimeRiskCoverageStatusPanel, WorkflowRuntimeScenarioCandidateCard } from './RuntimeScenarioCandidateCard';
import { WorkflowTargetedValidationSection } from './TargetedValidationCard';

function formatPercent(value) {
  if (typeof value !== 'number') {
    return 'n/a';
  }
  return `${Math.round(value * 100)}%`;
}

function formatMetricValue(value, unit) {
  if (typeof value !== 'number') {
    return 'n/a';
  }
  return `${value} ${unit || ''}`.trim();
}

function proofStatusLabel(status) {
  switch (status) {
    case 'comparable':
      return 'Comparable';
    case 'partial':
      return 'Partial';
    case 'insufficient_data':
      return 'Low data';
    case 'unavailable':
    default:
      return 'Unavailable';
  }
}

function proofDirectionLabel(direction) {
  switch (direction) {
    case 'improved':
      return 'Improved';
    case 'regressed':
      return 'Regressed';
    case 'stable':
      return 'Stable';
    case 'unavailable':
    default:
      return 'Unavailable';
  }
}

function analysisWindowLabel(window) {
  if (!window || !window.started_at || !window.ended_at) {
    return 'Waiting for completed analysis.';
  }
  return `${formatEvidenceDateTime(window.started_at)} to ${formatEvidenceDateTime(window.ended_at)}`;
}

function findingStatusLabel(status) {
  switch (status) {
    case 'in_progress':
      return 'In progress';
    case 'resolved':
      return 'Resolved';
    case 'open':
    default:
      return 'Open';
  }
}

function planStatusLabel(status) {
  switch (status) {
    case 'awaiting_post_fix_evidence':
      return 'Awaiting evidence';
    case 'review_ready':
      return 'Review ready';
    case 'monitoring':
      return 'Monitoring';
    case 'planned':
    default:
      return 'Planned';
  }
}

function WorkflowImprovementPlanSummary({ selectedSystem, summary }) {
  if (!summary) {
    return null;
  }
  return (
    <section className="detail-section">
      <div className="chapter-header">
        <div>
          <p className="chapter-kicker">Plans</p>
          <h3>Improvement plans</h3>
        </div>
      </div>
      <div className="detail-metrics-grid improvement-loop-counts">
        <div className="detail-card detail-stat-card">
          <strong>{summary.active_count}</strong>
          <p>Active plans</p>
        </div>
        <div className="detail-card detail-stat-card">
          <strong>{summary.awaiting_evidence_count}</strong>
          <p>Awaiting evidence</p>
        </div>
        <div className="detail-card detail-stat-card">
          <strong>{summary.review_ready_count}</strong>
          <p>Ready to review</p>
        </div>
        <div className="detail-card detail-stat-card">
          <strong>{summary.monitoring_count}</strong>
          <p>Monitoring</p>
        </div>
      </div>
      {summary.top_items?.length ? (
        <div className="stack compact-stack">
          {summary.top_items.map((plan) => (
            <article className="detail-card workflow-link-card" key={plan.id}>
              <div className="workflow-mode-header">
                <div>
                  <strong>{plan.finding_title || 'Workflow improvement plan'}</strong>
                  <p>{plan.intended_fix_summary}</p>
                  {plan.outcome_summary ? <p className="overview-note">{plan.outcome_summary}</p> : null}
                </div>
                <span className="chip chip-emphasis">{planStatusLabel(plan.status)}</span>
              </div>
              <div className="form-actions">
                {plan.finding_id ? (
                  <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/findings/${plan.finding_id}`}>
                    Open finding
                  </a>
                ) : null}
                {plan.eval_pack_id ? (
                  <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/eval-packs/${plan.eval_pack_id}`}>
                    Open validation
                  </a>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="empty-state-card">
          <strong>No improvement plans yet</strong>
          <p>Plans will appear here after a team records an intended improvement from a finding.</p>
        </div>
      )}
    </section>
  );
}

function WorkflowRuntimeRiskCoverageSummary({ summary }) {
  if (!summary || summary.active_runtime_risk_count <= 0) {
    return null;
  }
  return (
    <section className="detail-section">
      <div className="chapter-header">
        <div>
          <p className="chapter-kicker">Evaluation coverage</p>
          <h3>Runtime risk coverage</h3>
        </div>
      </div>
      <div className="detail-metrics-grid">
        <div className="detail-card detail-stat-card">
          <strong>{summary.active_runtime_risk_count}</strong>
          <p>Runtime risks</p>
        </div>
        <div className="detail-card detail-stat-card">
          <strong>{summary.reviewed_risk_count}</strong>
          <p>Reviewed risks</p>
        </div>
        <div className="detail-card detail-stat-card">
          <strong>{summary.sent_risk_count}</strong>
          <p>Sent to coverage</p>
        </div>
        <div className="detail-card detail-stat-card">
          <strong>{summary.imported_summary_count}</strong>
          <p>Imported summaries</p>
        </div>
      </div>
      <RuntimeRiskCoverageStatusPanel coverage={summary.top_status} />
    </section>
  );
}

export default function WorkflowDetailView({
  selectedSystem,
  workflowDetail,
  banner,
  findingsHref,
  evalPackPreview,
  previewFindingId,
  previewLoadingFindingId,
  evalPackActionLoadingFindingId,
  evalPackActionError,
  onPreviewEvalPack,
  onCreateEvalPack,
}) {
  const evidenceGuidance = buildWorkflowEvidenceGuidance(workflowDetail);

  return (
    <section className="content-grid">
      <aside className="panel side-panel">
        <p className="detail-kicker">Workflow detail</p>
        <h2>Navigation</h2>
        <div className="stack compact-stack">
          <a className="finding-link" href={`#/systems/${selectedSystem.id}/workflows`}>
            Back to workflows
          </a>
          <a className="finding-link" href={findingsHref(selectedSystem.id, { workflowId: workflowDetail.workflow.id })}>
            Open workflow findings
          </a>
          <a className="finding-link" href={`#/systems/${selectedSystem.id}`}>
            Back to overview
          </a>
        </div>
      </aside>

      <div className="panel detail-panel">
        {banner}
        <p className="detail-kicker">Workflow</p>
        <h2>{workflowDetail.workflow.name}</h2>
        <p className="panel-copy">{workflowDetail.workflow.description || 'No workflow description provided yet.'}</p>

        <div className="detail-summary-grid">
          <div className="context-banner">
            Status <strong>{workflowDetail.workflow.status}</strong>
          </div>
          <div className="context-banner">
            Success rate <strong>{formatPercent(workflowDetail.success_rate)}</strong>
          </div>
          <div className="context-banner">
            Active findings <strong>{workflowDetail.active_finding_count}</strong>
          </div>
          <div className="context-banner">
            Runs in window <strong>{workflowDetail.analysis_window.run_count}</strong>
          </div>
          <div className="context-banner">
            Runtime freshness <strong><EvidencePostureChip posture={workflowDetail.evidence_posture} /></strong>
          </div>
          <div className="context-banner">
            Last analysed <strong>{formatEvidenceDateTime(workflowDetail.evidence_posture?.last_analysed_at)}</strong>
          </div>
          <div className="context-banner">
            Snapshot refreshed <strong>{formatEvidenceDateTime(workflowDetail.last_refreshed_at)}</strong>
          </div>
        </div>

        <section className="detail-section">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Summary</p>
              <h3>Reliability verdict</h3>
            </div>
          </div>
          {workflowDetail.reliability_verdict ? (
            <div className="reliability-panel">
              <div className="workflow-mode-header">
                <div>
                  <strong>{workflowDetail.reliability_verdict.label}</strong>
                  <p>{workflowDetail.reliability_verdict.summary}</p>
                </div>
                <span className={reliabilityVerdictChipClassName(workflowDetail.reliability_verdict.overall_verdict)}>
                  {workflowDetail.reliability_verdict.label}
                </span>
              </div>
              <p>{workflowDetail.reliability_verdict.reason}</p>
              {workflowDetail.reliability_verdict.confidence_note ? (
                <p className="overview-note">{workflowDetail.reliability_verdict.confidence_note}</p>
              ) : null}

              <div className="reliability-support-grid">
                <div className="detail-card">
                  <strong>Operational evidence</strong>
                  <p>{operationalEvidenceSummary(workflowDetail.reliability_verdict)}</p>
                </div>
                <div className="detail-card">
                  <strong>Coverage summary</strong>
                  <p>{validationCoverageSummary(workflowDetail.reliability_verdict)}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="empty-state-card">
              <strong>No workflow claim yet</strong>
              <p>This workflow needs more completed analysis before a bounded reliability verdict can be shown.</p>
            </div>
          )}

          <div className="stack compact-stack">
            <div className="detail-card">
              <strong>Analysis context</strong>
              <p>{analysisWindowLabel(workflowDetail.analysis_window)}</p>
              <p>{workflowDetail.trend.summary}</p>
              {workflowDetail.trend.low_data_reason ? <p className="overview-note">{workflowDetail.trend.low_data_reason}</p> : null}
            </div>
          </div>
        </section>

        <WorkflowImprovementLoopPanel summary={workflowDetail.improvement_loop_summary} />

        <WorkflowImprovementPlanSummary selectedSystem={selectedSystem} summary={workflowDetail.improvement_plan_summary} />

        <section className="detail-section">
          <EvidenceNeededGuidanceCard
            title="Selective review evidence"
            canSay={evidenceGuidance.canSay}
            cannotSay={evidenceGuidance.cannotSay}
            nextSteps={evidenceGuidance.nextSteps}
          />
        </section>

        <SuspiciousTracePackPanel pack={workflowDetail.suspicious_trace_pack} selectedSystem={selectedSystem} defaultOpen />
        <WorkflowRuntimeScenarioCandidateCard candidate={workflowDetail.runtime_scenario_candidate} selectedSystem={selectedSystem} />
        <WorkflowRuntimeRiskCoverageSummary summary={workflowDetail.runtime_risk_coverage_summary} />

        {workflowDetail.validation_coverage ? (
          <section className="detail-section">
            <div className="chapter-header">
              <div>
                <p className="chapter-kicker">Validation</p>
                <h3>Validation coverage</h3>
              </div>
            </div>
            <div className="stack compact-stack">
              <div className="detail-card">
                <div className="workflow-mode-header">
                  <strong>{workflowDetail.validation_coverage.summary}</strong>
                  <span className="chip chip-emphasis">{workflowDetail.validation_coverage.confidence} confidence</span>
                </div>
                <p>{workflowDetail.validation_coverage.reason}</p>
                {workflowDetail.validation_coverage.low_data_reasons?.length ? (
                  <p className="overview-note">{workflowDetail.validation_coverage.low_data_reasons.join(' ')}</p>
                ) : null}
              </div>
              <div className="detail-metrics-grid">
                <div className="detail-card detail-stat-card">
                  <strong>{workflowDetail.validation_coverage.covered_behavior_count}</strong>
                  <p>Covered behaviors</p>
                </div>
                <div className="detail-card detail-stat-card">
                  <strong>{workflowDetail.validation_coverage.uncovered_behavior_count}</strong>
                  <p>Uncovered behaviors</p>
                </div>
                <div className="detail-card detail-stat-card">
                  <strong>{workflowDetail.validation_coverage.validated_item_count}</strong>
                  <p>Labeled items</p>
                </div>
              </div>
            </div>
          </section>
        ) : null}

        <WorkflowTargetedValidationSection
          selectedSystem={selectedSystem}
          workflowDetail={workflowDetail}
          preview={evalPackPreview}
          previewFindingId={previewFindingId}
          previewLoadingFindingId={previewLoadingFindingId}
          actionLoadingFindingId={evalPackActionLoadingFindingId}
          actionError={evalPackActionError}
          onPreview={onPreviewEvalPack}
          onCreate={onCreateEvalPack}
        />

        {workflowDetail.validation_proof ? (
          <section className="detail-section">
            <div className="chapter-header">
              <div>
                <p className="chapter-kicker">Proof</p>
                <h3>Improvement proof</h3>
              </div>
            </div>
            <div className="detail-card">
              <div className="workflow-mode-header">
                <strong>{workflowDetail.validation_proof.summary}</strong>
                <div className="chip-row">
                  <span className="chip">{proofStatusLabel(workflowDetail.validation_proof.status)}</span>
                  <span className="chip chip-emphasis">{proofDirectionLabel(workflowDetail.validation_proof.validation_direction)}</span>
                </div>
              </div>
              <p>
                Confidence {workflowDetail.validation_proof.coverage_confidence}
                {workflowDetail.validation_proof.low_data_reason ? ` · ${workflowDetail.validation_proof.low_data_reason}` : ''}
              </p>
              <div className="detail-metrics-grid">
                <div className="detail-card detail-stat-card">
                  <strong>{formatPercent(workflowDetail.validation_proof.current_pass_rate)}</strong>
                  <p>Current pass rate</p>
                </div>
                <div className="detail-card detail-stat-card">
                  <strong>{formatPercent(workflowDetail.validation_proof.previous_pass_rate)}</strong>
                  <p>Previous pass rate</p>
                </div>
                <div className="detail-card detail-stat-card">
                  <strong>{workflowDetail.validation_proof.current_labeled_item_count}</strong>
                  <p>Current labeled</p>
                </div>
                <div className="detail-card detail-stat-card">
                  <strong>{workflowDetail.validation_proof.previous_labeled_item_count}</strong>
                  <p>Previous labeled</p>
                </div>
              </div>
            </div>
          </section>
        ) : null}

        <section className="detail-section">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Evidence</p>
              <h3>Workflow evidence</h3>
              <p className="panel-copy">Review the measured signals and representative runs behind the latest workflow analysis.</p>
            </div>
          </div>

          <h3 className="subsection-heading">Workflow signals</h3>
          {workflowDetail.signal_cards.length ? (
            <div className="detail-metrics-grid">
              {workflowDetail.signal_cards.map((signalCard) => (
                <div className="detail-card detail-stat-card" key={signalCard.signal_type}>
                  <strong>{signalCard.signal_type === 'completion_rate' ? formatPercent(signalCard.metric_value) : formatMetricValue(signalCard.metric_value, signalCard.unit)}</strong>
                  <p>{signalCard.label}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state-card">
              <strong>No workflow signals yet</strong>
              <p>This workflow does not have persisted workflow signals for the latest completed analysis yet.</p>
            </div>
          )}

          <h3 className="subsection-heading">Representative traces</h3>
          {workflowDetail.representative_traces.length ? (
            <div className="stack compact-stack bounded-secondary-collection" data-testid="workflow-representative-traces-collection">
              {workflowDetail.representative_traces.map((trace) => (
                <a
                  className="detail-card workflow-link-card"
                  href={buildRunTraceHref({ systemId: selectedSystem.id, runId: trace.run_id })}
                  key={trace.run_id}
                  rel="noreferrer"
                  target="_blank"
                >
                  <strong>{trace.label}</strong>
                  <p>
                    Status {trace.status} · Retries {trace.retry_count} · Steps {trace.total_step_count}
                  </p>
                  <p>{trace.source_run_key ? `Source key: ${trace.source_run_key}` : `Run ${trace.run_id}`}</p>
                </a>
              ))}
            </div>
          ) : (
            <div className="empty-state-card">
              <strong>No representative traces yet</strong>
              <p>Review the workflow verdict and linked findings first. Representative trace links appear once the latest analysis can pick runs with enough persisted context.</p>
            </div>
          )}
        </section>

        <section className="detail-section">
          <div className="chapter-header">
            <div>
              <p className="chapter-kicker">Action</p>
              <h3>Where to follow up</h3>
            </div>
          </div>

          <div className="detail-section">
            <h3>Runtime patterns</h3>
          </div>
          {workflowDetail.failure_modes.length ? (
            <div className="stack compact-stack">
              {workflowDetail.failure_modes.map((mode) => (
                <article className="detail-card workflow-mode-card" key={mode.mode_key}>
                  <div className="workflow-mode-header">
                    <strong>{mode.title}</strong>
                    <span className="chip">{mode.severity}</span>
                  </div>
                  <p>{mode.summary}</p>
                  <div className="chip-row">
                    {mode.finding_count ? <span className="chip">Findings {mode.finding_count}</span> : null}
                    {mode.run_count ? <span className="chip">Runs {mode.run_count}</span> : null}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="empty-state-card">
              <strong>No runtime patterns highlighted</strong>
              <p>This workflow currently has no active findings and no non-success runs in the latest analysis window.</p>
            </div>
          )}

          <div className="detail-section">
            <h3>Related findings</h3>
          </div>
          {workflowDetail.related_findings.length ? (
            <div className="stack compact-stack">
              {workflowDetail.related_findings.map((finding) => (
                <a className="detail-card workflow-link-card" href={`#/systems/${selectedSystem.id}/findings/${finding.id}`} key={finding.id}>
                  <strong>{finding.title}</strong>
                  <p>{finding.summary}</p>
                  <div className="chip-row">
                    <span className="chip">{finding.severity}</span>
                    <span className="chip">{findingStatusLabel(finding.status)}</span>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="empty-state-card">
              <strong>No related findings yet</strong>
              <p>No active findings are currently linked to this workflow.</p>
            </div>
          )}
        </section>
      </div>
    </section>
  );
}
